import { RefrigerationUnitSafetyThreshold, type RefrigerationUnit } from '@/data/schemas';
import type { RefrigerationUnitTechnicalInput } from '@/data/repos/refrigerationUnitsRepo';
import { newId } from '@/utils/ids';
import {
  type RefrigerationUnitFormValues,
  RefrigerationUnitFormError,
  toNonNegativeInt,
  toOptionalInt,
  trim,
} from './shared';
import { normalizeThresholdActionKeys } from '@/data/thresholdActions';

export const buildSafetyDefaults = (): Pick<
  RefrigerationUnitFormValues,
  | 'detectorsToxicCount'
  | 'detectorsExplosiveCount'
  | 'detectorsCeilingCount'
  | 'detectorsHumanCount'
  | 'detectorPositions'
  | 'thresholds'
  | 'calibrationNotes'
  | 'emergencyDialerPresent'
  | 'emergencyGeneratorPresent'
  | 'emergencyGuardServicePresent'
  | 'emergencyNotes'
  | 'emergencyEeTripButtons'
  | 'emergencyEeTripDetector'
  | 'emergencyElettroserraturaPresent'
  | 'emergencyLightingPresent'
  | 'emergencyLightingAtex'
  | 'sgancioAutomaticoPresent'
  | 'sogliaSgancioAutomatico'
  | 'pulsanteSgancioPresent'
  | 'powerPreferredLine'
  | 'powerGenerator'
  | 'powerUps'
  | 'powerNotes'
  | 'detectionMaintenanceName'
  | 'detectionMaintenanceNotes'
> => ({
  detectorsToxicCount: 0,
  detectorsExplosiveCount: 0,
  detectorsCeilingCount: null,
  detectorsHumanCount: null,
  detectorPositions: [],
  thresholds: [],
  calibrationNotes: '',
  emergencyDialerPresent: false,
  emergencyGeneratorPresent: false,
  emergencyGuardServicePresent: false,
  emergencyNotes: '',
  emergencyEeTripButtons: false,
  emergencyEeTripDetector: false,
  emergencyElettroserraturaPresent: false,
  emergencyLightingPresent: false,
  emergencyLightingAtex: false,
  sgancioAutomaticoPresent: false,
  sogliaSgancioAutomatico: '',
  pulsanteSgancioPresent: false,
  powerPreferredLine: false,
  powerGenerator: false,
  powerUps: false,
  powerNotes: '',
  detectionMaintenanceName: '',
  detectionMaintenanceNotes: '',
});

export const buildSafetyValues = (
  refrigerationUnit: RefrigerationUnit
): Pick<
  RefrigerationUnitFormValues,
  | 'detectorsToxicCount'
  | 'detectorsExplosiveCount'
  | 'detectorsCeilingCount'
  | 'detectorsHumanCount'
  | 'detectorPositions'
  | 'thresholds'
  | 'calibrationNotes'
  | 'emergencyDialerPresent'
  | 'emergencyGeneratorPresent'
  | 'emergencyGuardServicePresent'
  | 'emergencyNotes'
  | 'emergencyEeTripButtons'
  | 'emergencyEeTripDetector'
  | 'emergencyElettroserraturaPresent'
  | 'emergencyLightingPresent'
  | 'emergencyLightingAtex'
  | 'sgancioAutomaticoPresent'
  | 'sogliaSgancioAutomatico'
  | 'pulsanteSgancioPresent'
  | 'powerPreferredLine'
  | 'powerGenerator'
  | 'powerUps'
  | 'powerNotes'
  | 'detectionMaintenanceName'
  | 'detectionMaintenanceNotes'
> => {
  const safety = refrigerationUnit.technical?.safety;
  const existingThresholds = (safety?.detectors?.thresholds ?? []).map((threshold) => ({
    id: threshold.id ?? newId(),
    thresholdType: threshold.thresholdType ?? 'tossicita',
    unit: threshold.unit,
    value: threshold.value ?? null,
    actions: normalizeThresholdActionKeys(threshold.actions ?? []),
  }));

  const emergency = safety?.emergencySystems;
  const power = safety?.powerBackup;

  return {
    detectorsToxicCount: safety?.detectors?.toxicCount ?? 0,
    detectorsExplosiveCount: safety?.detectors?.explosiveCount ?? 0,
    detectorsCeilingCount: safety?.detectors?.ceilingCount ?? null,
    detectorsHumanCount: safety?.detectors?.humanHeightCount ?? null,
    detectorPositions: safety?.detectors?.positions ?? [],
    thresholds:
      existingThresholds.length > 0
        ? existingThresholds
        : [
            {
              id: newId(),
              thresholdType: 'tossicita',
              unit: 'ppm',
              value: 300,
              actions: ['siren'],
            },
          ],
    calibrationNotes: safety?.detectors?.calibrationNotes ?? '',
    emergencyDialerPresent: emergency?.dialerPresent ?? false,
    emergencyGeneratorPresent: emergency?.generatorPresent ?? false,
    emergencyGuardServicePresent: emergency?.guardServicePresent ?? false,
    emergencyNotes: emergency?.notes ?? '',
    emergencyEeTripButtons: emergency?.eeTripButtons ?? false,
    emergencyEeTripDetector: emergency?.eeTripDetector ?? false,
    emergencyElettroserraturaPresent: emergency?.elettroserraturaPresent ?? false,
    emergencyLightingPresent: emergency?.emergencyLightingPresent ?? false,
    emergencyLightingAtex: emergency?.emergencyLightingAtex ?? false,
    sgancioAutomaticoPresent: emergency?.sgancioAutomaticoPresent ?? false,
    sogliaSgancioAutomatico: emergency?.sogliaSgancioAutomatico ?? '',
    pulsanteSgancioPresent: emergency?.pulsanteSgancioPresent ?? false,
    powerPreferredLine: power?.preferredLine ?? false,
    powerGenerator: power?.generator ?? false,
    powerUps: power?.ups ?? false,
    powerNotes: power?.notes ?? '',
    detectionMaintenanceName: safety?.detectionMaintenance?.name ?? '',
    detectionMaintenanceNotes: safety?.detectionMaintenance?.notes ?? '',
  };
};

export const safetyToInput = (
  values: RefrigerationUnitFormValues
): Pick<
  NonNullable<RefrigerationUnitTechnicalInput['safety']>,
  'detectors' | 'powerBackup' | 'emergencySystems' | 'detectionMaintenance'
> => {
  const detectorPositions = (values.detectorPositions ?? [])
    .map((item) => trim(item))
    .filter((item): item is string => Boolean(item));

  const thresholds = values.thresholds.map((threshold) => {
    if (!threshold.actions.length) {
      throw new RefrigerationUnitFormError(
        "Specifica almeno un'azione per la soglia",
        'thresholds'
      );
    }
    const actions = Array.from(new Set(normalizeThresholdActionKeys(threshold.actions)));
    const unit = threshold.unit ?? 'ppm';
    const thresholdType = threshold.thresholdType ?? 'tossicita';
    const value = threshold.value ?? 0;
    if (!Number.isFinite(value) || value <= 0) {
      throw new RefrigerationUnitFormError('Inserisci un valore di soglia valido', 'thresholds');
    }
    const parsed = RefrigerationUnitSafetyThreshold.parse({
      id: threshold.id ?? newId(),
      thresholdType,
      unit,
      value,
      actions,
    });
    return {
      id: parsed.id,
      thresholdType: parsed.thresholdType,
      unit: parsed.unit,
      value: parsed.value,
      actions: parsed.actions,
    } satisfies RefrigerationUnitSafetyThreshold;
  });

  const detectors = {
    toxicCount: toNonNegativeInt(values.detectorsToxicCount, 0),
    explosiveCount: toNonNegativeInt(values.detectorsExplosiveCount, 0),
    positions: detectorPositions,
    thresholds,
    calibrationNotes: trim(values.calibrationNotes),
    ceilingCount: toOptionalInt(values.detectorsCeilingCount ?? undefined),
    humanHeightCount: toOptionalInt(values.detectorsHumanCount ?? undefined),
  };

  const emergencySystems =
    values.emergencyDialerPresent ||
    values.emergencyGeneratorPresent ||
    values.emergencyGuardServicePresent ||
    values.emergencyNotes ||
    values.emergencyEeTripButtons ||
    values.emergencyEeTripDetector ||
    values.emergencyElettroserraturaPresent ||
    values.emergencyLightingPresent ||
    values.emergencyLightingAtex ||
    values.sgancioAutomaticoPresent ||
    trim(values.sogliaSgancioAutomatico) ||
    values.pulsanteSgancioPresent
      ? {
          dialerPresent: values.emergencyDialerPresent,
          generatorPresent: values.emergencyGeneratorPresent,
          guardServicePresent: values.emergencyGuardServicePresent,
          notes: trim(values.emergencyNotes),
          eeTripButtons: values.emergencyEeTripButtons || undefined,
          eeTripDetector: values.emergencyEeTripDetector || undefined,
          elettroserraturaPresent: values.emergencyElettroserraturaPresent || undefined,
          emergencyLightingPresent: values.emergencyLightingPresent || undefined,
          emergencyLightingAtex: values.emergencyLightingAtex || undefined,
          sgancioAutomaticoPresent: values.sgancioAutomaticoPresent || undefined,
          sogliaSgancioAutomatico: trim(values.sogliaSgancioAutomatico),
          pulsanteSgancioPresent: values.pulsanteSgancioPresent || undefined,
        }
      : undefined;

  const powerBackup =
    values.powerPreferredLine || values.powerGenerator || values.powerUps || trim(values.powerNotes)
      ? {
          preferredLine: values.powerPreferredLine,
          generator: values.powerGenerator,
          ups: values.powerUps,
          notes: trim(values.powerNotes),
        }
      : undefined;

  const detectionMaintenance =
    trim(values.detectionMaintenanceName) || trim(values.detectionMaintenanceNotes)
      ? {
          name: trim(values.detectionMaintenanceName),
          notes: trim(values.detectionMaintenanceNotes),
        }
      : undefined;

  return {
    detectors,
    powerBackup,
    emergencySystems,
    detectionMaintenance,
  };
};
